import { useEffect, useState } from 'react';
import { db, auth } from '../FirebaseAuthConfig.js';
import { doc, onSnapshot, getDoc, collection, getDocs } from 'firebase/firestore';

// Max points configuration
const MAX_INDIVIDUAL_POINTS = 128; // 30 Work + 60 Abstain + 30 Renew + 8 Bonus
const MAX_TEAM_POINTS = 256; // 128 * 2 members

// Tier thresholds
const TIER_1 = 85;
const TIER_2 = 100;
const TIER_3 = 115;

function ProgressBlock({ className }) {
    const [currentAuthUser, setCurrentAuthUser] = useState(null);
    
    // Individual stats
    const [points, setPoints] = useState(0);
    const [workPoints, setWorkPoints] = useState(0);
    const [abstainPoints, setAbstainPoints] = useState(0);
    const [renewPoints, setRenewPoints] = useState(0);
    const [bonusPoints, setBonusPoints] = useState(0);
    const [totalMeps, setTotalMeps] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    
    // Team info
    const [isIndividual, setIsIndividual] = useState(true);
    const [teamName, setTeamName] = useState('');
    const [teamPoints, setTeamPoints] = useState(0);
    const [teammateId, setTeammateId] = useState(null);
    
    // Teammate stats (for overlay)
    const [showTeammate, setShowTeammate] = useState(false);
    const [teammateData, setTeammateData] = useState(null);
    const [loadingTeammate, setLoadingTeammate] = useState(false);

    // Auto-refresh at midnight
    useEffect(() => {
        const interval = setInterval(() => {
            const now = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h12' });
            if (now === '12:01:00 AM') {
                window.location.reload();
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Auth listener
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((currentUser) => {
            if (currentUser) {
                setCurrentAuthUser(currentUser);
            }
        });
        return () => unsubscribe();
    }, []);

    // Fetch user data and set up listeners
    useEffect(() => {
        if (!currentAuthUser) return;

        // Listen to user's points pool
        const pointsPoolRef = doc(db, `userCollection/${currentAuthUser.uid}/challengeData/everydayWarship/pointsPool/master`);
        const unsubscribePoints = onSnapshot(pointsPoolRef, (snapshot) => {
            const data = snapshot.data() || {};
            setPoints(data.points || 0);
            setWorkPoints(data.workPoints || 0);
            setAbstainPoints(data.abstainPoints || 0);
            setRenewPoints(data.renewPoints || 0);
            setBonusPoints(data.bonusPoints || 0);
            setTotalMeps(data.meps || 0);
            setTotalSteps(data.steps || 0);
        });

        // Get user's challenge data (team info)
        const userChallengeRef = doc(db, `userCollection/${currentAuthUser.uid}/challengeData/everydayWarship`);
        const unsubscribeChallenge = onSnapshot(userChallengeRef, async (snapshot) => {
            const data = snapshot.data() || {};
            
            if (data.participationType === 'individual') {
                setIsIndividual(true);
            } else {
                setIsIndividual(false);
                const teamId = data.team;
                
                if (teamId) {
                    // Listen to team data
                    const teamRef = doc(db, `challengeCollection/everydayWarship/teams/${teamId}`);
                    onSnapshot(teamRef, async (teamSnap) => {
                        const teamData = teamSnap.data() || {};
                        setTeamName(teamData.name || '');
                        setTeamPoints(teamData.points || 0);
                        
                        // Fetch members from subcollection to find teammate
                        const membersRef = collection(db, `challengeCollection/everydayWarship/teams/${teamId}/members`);
                        const membersSnap = await getDocs(membersRef);
                        const memberIds = membersSnap.docs.map(doc => doc.id);
                        const teammate = memberIds.find(id => id !== currentAuthUser.uid);
                        setTeammateId(teammate || null);
                    });
                }
            }
        });

        return () => {
            unsubscribePoints();
            unsubscribeChallenge();
        };
    }, [currentAuthUser]);

    // Fetch teammate's stats
    const fetchTeammateStats = async () => {
        if (!teammateId) return;
        
        setLoadingTeammate(true);
        try {
            // Get teammate's display name
            const teammateUserRef = doc(db, `userCollection/${teammateId}`);
            const teammateUserSnap = await getDoc(teammateUserRef);
            const teammateUserData = teammateUserSnap.data() || {};
            
            // Get teammate's points pool
            const teammatePoolRef = doc(db, `userCollection/${teammateId}/challengeData/everydayWarship/pointsPool/master`);
            const teammatePoolSnap = await getDoc(teammatePoolRef);
            const poolData = teammatePoolSnap.data() || {};
            
            setTeammateData({
                displayName: teammateUserData.displayName || 'Teammate',
                points: poolData.points || 0,
                workPoints: poolData.workPoints || 0,
                abstainPoints: poolData.abstainPoints || 0,
                renewPoints: poolData.renewPoints || 0,
                bonusPoints: poolData.bonusPoints || 0,
                meps: poolData.meps || 0,
                steps: poolData.steps || 0,
            });
        } catch (error) {
            console.error('Error fetching teammate stats:', error);
        }
        setLoadingTeammate(false);
        setShowTeammate(true);
    };

    // Get current tier
    const getTier = (pts) => {
        if (pts >= TIER_3) return { tier: 3, label: 'Gold', color: 'text-yellow-500' };
        if (pts >= TIER_2) return { tier: 2, label: 'Team', color: 'text-blue-500' };
        if (pts >= TIER_1) return { tier: 1, label: 'Individual', color: 'text-green-500' };
        return { tier: 0, label: '', color: '' };
    };

    // Format large numbers
    const formatNumber = (num) => {
        return num.toLocaleString();
    };

    // Progress bar component with tier markers
    const ProgressBar = ({ current, max, showTiers = false }) => {
        const percentage = Math.min((current / max) * 100, 100);
        
        return (
            <div className="relative w-full">
                <div className="w-full h-5 rounded-full bg-neutral-300 dark:bg-neutral-600 overflow-hidden">
                    <div 
                        className="h-full rounded-full bg-[#FF671F] transition-all duration-500"
                        style={{ width: `${percentage}%` }}
                    />
                </div>
                {showTiers && (
                    // <div className="absolute top-0 left-0 w-full h-5 pointer-events-none">
                    //     {/* Tier 1 marker at 85/128 */}
                    //     <div 
                    //         className="absolute top-0 h-full w-0.5 bg-green-500"
                    //         style={{ left: `${(TIER_1 / MAX_INDIVIDUAL_POINTS) * 100}%` }}
                    //         title="Tier 1: 85 pts"
                    //     />
                    //     {/* Tier 2 marker at 100/128 */}
                    //     <div 
                    //         className="absolute top-0 h-full w-0.5 bg-blue-500"
                    //         style={{ left: `${(TIER_2 / MAX_INDIVIDUAL_POINTS) * 100}%` }}
                    //         title="Tier 2: 100 pts"
                    //     />
                    //     {/* Tier 3 marker at 115/128 */}
                    //     <div 
                    //         className="absolute top-0 h-full w-0.5 bg-yellow-500"
                    //         style={{ left: `${(TIER_3 / MAX_INDIVIDUAL_POINTS) * 100}%` }}
                    //         title="Tier 3: 115 pts"
                    //     />
                    // </div>
                    <></>
                )}
            </div>
        );
    };

    // Stat item component
    const StatItem = ({ label, value, subValue, highlight = false }) => (
        <div className="flex flex-col items-center">
            <span className={`font-sans text-2xl font-bold ${highlight ? 'text-[#FF671F]' : 'text-neutral-800 dark:text-white'}`}>
                {typeof value === 'number' ? formatNumber(value) : value}
            </span>
            <span className="font-sans text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</span>
            {subValue && (
                <span className="font-sans text-xs text-neutral-400 dark:text-neutral-500">{subValue}</span>
            )}
        </div>
    );

    // Stats grid component (reused for individual and teammate)
    const StatsGrid = ({ data }) => (
        <div className="w-full grid grid-cols-4 gap-2 mt-3">
            <StatItem label="Work" value={data.workPoints} subValue="/30" />
            <StatItem label="Abstain" value={data.abstainPoints} subValue="/60" />
            <StatItem label="Renew" value={data.renewPoints} subValue="/30" />
            <StatItem label="Bonus" value={data.bonusPoints} subValue="/8" />
        </div>
    );

    // Additional stats row
    const AdditionalStats = ({ data }) => (
        <div className="w-full flex flex-row justify-around mt-3 pt-3 border-t border-neutral-300 dark:border-neutral-600">
            <StatItem label="Total MEPs" value={data.meps || data.totalMeps || 0} />
            <StatItem label="Total Steps" value={data.steps || data.totalSteps || 0} />
        </div>
    );

    const currentTier = getTier(points);

    return (
        <div className={`w-full rounded-2xl bg-neutral-200 dark:bg-neutral-700 p-4 relative overflow-hidden ${className || ''}`}>
            {/* Main Content */}
            <div className={`transition-opacity duration-200 ${showTeammate ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                {/* Individual Progress */}
                <div className="mb-4">
                    <div className="flex flex-row justify-between items-center mb-2">
                        <h2 className="font-sans text-lg font-bold text-neutral-700 dark:text-neutral-200">
                            Your Progress
                        </h2>
                        <div className="flex items-center gap-2">
                            {currentTier.tier > 0 && (
                                <span className={`font-sans text-sm font-bold ${currentTier.color}`}>
                                    {currentTier.label} Tier
                                </span>
                            )}
                            <span className="font-sans text-sm font-bold text-[#FF671F]">
                                {points}/{MAX_INDIVIDUAL_POINTS}
                            </span>
                        </div>
                    </div>
                    <ProgressBar current={points} max={MAX_INDIVIDUAL_POINTS} showTiers />
                    
                    {/* Tier legend */}
                    {/* <div className="flex flex-row justify-end gap-3 mt-1">
                        <span className="font-sans text-xs text-green-500">85</span>
                        <span className="font-sans text-xs text-blue-500">100</span>
                        <span className="font-sans text-xs text-yellow-500">115</span>
                    </div> */}
                </div>

                {/* Team Progress (if applicable) */}
                {!isIndividual && (
                    <div className="mb-4">
                        <div className="flex flex-row justify-between items-center mb-2">
                            <h2 className="font-sans text-lg font-bold text-neutral-700 dark:text-neutral-200">
                                Team Progress - {' '}
                                <button 
                                    className="underline cursor-pointer bg-transparent border-none font-sans text-lg font-bold text-neutral-700 dark:text-neutral-200 hover:text-[#FF671F] dark:hover:text-[#FF671F] transition-colors"
                                    onClick={fetchTeammateStats}
                                >
                                    {teamName}
                                </button>
                            </h2>
                            <span className="font-sans text-sm font-bold text-[#FF671F]">
                                {teamPoints}/{MAX_TEAM_POINTS}
                            </span>
                        </div>
                        <ProgressBar current={teamPoints} max={MAX_TEAM_POINTS} />
                    </div>
                )}

                {/* Stats Breakdown */}
                <h3 className="font-sans text-sm font-bold text-neutral-600 dark:text-neutral-300 mb-1">Your Stats</h3>
                <StatsGrid data={{ workPoints, abstainPoints, renewPoints, bonusPoints }} />
                <AdditionalStats data={{ totalMeps, totalSteps }} />
            </div>

            {/* Teammate Overlay */}
            {showTeammate && (
                <div className="absolute inset-0 bg-neutral-200 dark:bg-neutral-700 p-4 animate-fadeIn">
                    <div className="flex flex-row justify-between items-center mb-4">
                        <h2 className="font-sans text-lg font-bold text-neutral-700 dark:text-neutral-200">
                            {teammateData?.displayName || 'Teammate'}'s Progress
                        </h2>
                        <button 
                            className="w-8 h-8 rounded-full bg-neutral-300 dark:bg-neutral-600 flex items-center justify-center cursor-pointer border-none hover:bg-neutral-400 dark:hover:bg-neutral-500 transition-colors"
                            onClick={() => setShowTeammate(false)}
                        >
                            <span className="text-neutral-600 dark:text-neutral-300 text-lg leading-none">&times;</span>
                        </button>
                    </div>

                    {loadingTeammate ? (
                        <div className="w-full flex justify-center py-8">
                            <span className="font-sans text-sm text-neutral-500 dark:text-neutral-400">Loading...</span>
                        </div>
                    ) : teammateData ? (
                        <>
                            {/* Teammate Progress */}
                            <div className="mb-auto">
                                <div className="flex flex-row justify-end items-center mb-2">
                                    {/* <span className="font-sans text-sm text-neutral-600 dark:text-neutral-300">Progress</span> */}
                                    <span className="font-sans text-sm font-bold text-[#FF671F]">
                                        {teammateData.points}/{MAX_INDIVIDUAL_POINTS}
                                    </span>
                                </div>
                                <ProgressBar current={teammateData.points} max={MAX_INDIVIDUAL_POINTS} showTiers />
                            </div>

                            {/* Teammate Stats */}
                            <StatsGrid data={teammateData} />
                            <AdditionalStats data={teammateData} />
                        </>
                    ) : (
                        <div className="w-full flex justify-center py-8">
                            <span className="font-sans text-sm text-neutral-500 dark:text-neutral-400">Could not load teammate stats</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ProgressBlock;
